import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../../../../../AppContent";
import { Alert } from "../../../../../Elements/Alert/Alert";
import { Input } from "../../../../../Elements/Input";
import { IRefactionRequestItemNew } from "../../../../../types/IRefaction";
import { Form } from "../../../../Wrapper/FormWrapper";
import { getRefactionItemData } from "./refactionItemData";
import { useLocation } from "react-router-dom";

interface Props {
  item?: IRefactionRequestItemNew;
  items?: IRefactionRequestItemNew[];
  edit?: boolean;
  newForm?: boolean;
  invoiceUuids?: string[];
  setIsFormVisible?: (visible: boolean) => void;
  onSubmit?: (item: IRefactionRequestItemNew, callback: () => void) => void;
  error?: boolean;
}

export const RefactionItemsForm: FC<Props> = ({
  item,
  items,
  edit = false,
  newForm,
  invoiceUuids,
  setIsFormVisible,
  onSubmit,
  error,
}) => {
  const [formData, setFormData] = useState<IRefactionRequestItemNew>(
    item || {}
  );
  const [plantCultureLiterLeft, setPlantCultureLiterLeft] = useState<{
    [key: string]: number;
  }>({});
  const [landLiterLeft, setLandLiterLeft] = useState<{ [key: string]: number }>(
    {}
  );

  const { t } = useTranslation();
  const farmUuid = useContext(FarmContext).farm?.uuid!;
  const location = useLocation();
  const year = location.pathname.split("-")[1];

  useEffect(() => {
    if (formData.landObj?.value) {
      const landId = formData.landObj.value;
      const totalUsedLiters =
        items
          ?.filter((item) => item.landObj?.value === landId)
          .reduce((acc, item) => acc + (item.liter || 0), 0) || 0;

      setLandLiterLeft((prev) => ({
        ...prev,
        [landId]:
          formData.landObj?.literRecapitulation?.leftLiter - totalUsedLiters,
      }));
    }
  }, [formData.liter, formData.landObj, items]);

  useEffect(() => {
    if (!!formData.landPlantCulture?.plantCulture) {
      const landId = formData.landObj?.value;
      const landPlantCultureUuid = formData.landPlantCulture.plantCulture.uuid;
      const totalUsedLiters =
        items
          ?.filter((item) => item.landObj?.value === landId)
          ?.filter(
            (item) =>
              item.landPlantCulture?.plantCulture?.uuid === landPlantCultureUuid
          )
          .reduce((acc, item) => acc + (item.liter || 0), 0) || 0;

      setPlantCultureLiterLeft((prev) => ({
        ...prev,
        [landPlantCultureUuid]:
          formData.landPlantCulture?.literRecapitulation?.leftLiter! -
          totalUsedLiters,
      }));
    }
  }, [formData.liter, formData.landPlantCulture, formData.landObj, items]);

  const data = getRefactionItemData({
    formData,
    setFormData,
    t,
    edit,
    farmUuid,
    invoiceUuids,
    items,
    plantCultureLiterLeft,
    setPlantCultureLiterLeft,
    landLiterLeft,
    setLandLiterLeft,
    year,
  });

  return newForm ? (
    <>
      {!!formData.landPlantCulture?.literRecapitulation && (
        <Alert
          text={t("RemainLiters", {
            value: Math.min(
              landLiterLeft[formData.landObj?.value] || 0,
              plantCultureLiterLeft[
                formData.landPlantCulture?.plantCulture?.uuid
              ] || 0
            ),
          })}
          type="info"
          className="mb-2"
        />
      )}
      <Form
        data={data}
        edit={newForm || edit}
        setEdit={() => {}}
        submitButton={{
          editLabel: t(newForm ? "SaveLand" : "LandEdit"),
          disabled: formData?.liter! <= 0,
          onSubmit: () => {
            onSubmit &&
              onSubmit(formData, () => {
                setFormData(item || {});
                if (formData.landObj?.value && formData.liter && !error) {
                  setLandLiterLeft((prev) => ({
                    ...prev,
                    [formData.landObj?.value]:
                      (prev[formData.landObj?.value] || 0) - formData.liter!,
                  }));
                }
              });
          },
        }}
        onResetForm={() => {
          setFormData(item || {});
          setIsFormVisible && setIsFormVisible(false);
        }}
        isLoading={false}
      />
    </>
  ) : (
    <div className="w-600 mx-auto">
      {data.map((e) => (
        <Input key={e.label} component={e} />
      ))}
    </div>
  );
};
